import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { getRecentRequestTrace } from "@/lib/requestTrace";

function newIncidentId(): string {
  const bytes = new Uint8Array(5);
  if (typeof crypto !== "undefined" && "getRandomValues" in crypto) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(36).padStart(2, "0")).join("").slice(0, 8).toUpperCase();
}

// Global error boundary. Wraps the whole App so a render-time exception
// in any route renders a stable fallback instead of a white screen, and
// best-effort reports the error to the server so we can see production
// crashes without having to ask the user for a screenshot.

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message: string | null;
  incidentId: string | null;
}

export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, message: null, incidentId: null };

  static getDerivedStateFromError(err: unknown): State {
    return {
      hasError: true,
      message: err instanceof Error ? err.message : String(err),
      incidentId: newIncidentId(),
    };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Fire-and-forget report. Don't await — the boundary's job is to
    // render the fallback fast, not to wait on a network roundtrip.
    const payload = {
      incident_id: this.state.incidentId,
      message: error.message,
      stack: error.stack ?? null,
      component_stack: info.componentStack ?? null,
      url: typeof window !== "undefined" ? window.location.href : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      recent_requests: getRecentRequestTrace(),
    };
    try {
      void fetch("/v1/client-errors", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // fetch not available (SSR hydrate, very old browser) — ignore.
    }
  }

  override render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        role="alert"
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center"
      >
        <h1 className="text-2xl font-semibold">Something went wrong</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          The page ran into an unexpected error. The incident has been
          reported. You can refresh to try again, or return to the
          dashboard.
        </p>
        {this.state.incidentId && (
          <p className="text-xs text-muted-foreground">
            Reference: <code className="font-mono">{this.state.incidentId}</code>
          </p>
        )}
        {this.state.message && (
          <pre className="max-w-xl overflow-auto rounded-md border bg-muted p-3 text-left text-xs">
            {this.state.message}
          </pre>
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.location.reload()}>
            Refresh
          </Button>
          <Button onClick={() => (window.location.href = "/dashboard")}>
            Go to dashboard
          </Button>
        </div>
      </div>
    );
  }
}
